import React, { useEffect, useMemo } from "react";
// ⚠️ `StatusPill` and `WarningIcon` were dropped from this import on
// 2026-09-03 with the two status markers they drew (see `ItemCard`). So were
// `ITEM_STATUS` and `REVIEW_STATUS`, which this file only needed to map those
// markers' labels and colours.
import { CommentIcon } from "../../StrategyCanvas/StrategyCanvasHelpers";
import useExecutionBlueprintReviewStore, {
  useExecutionBlueprintReviewStoreSelector,
} from "./_redux/ExecutionBlueprintReviewStore";
import "./ExecutionBlueprintBlueprintMode.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Blueprint Mode - the read-only companion to Review Mode (COACH-API.md §1
// #7: "read-only view, items grouped by section, no review affordances").
//
// The layout is the Art of Scale "Execution Blueprint" wall chart: a BHAG
// banner across the top, then one column per WIG section with that section's
// items stacked underneath. Two deliberate departures from the source deck,
// both on the user's instruction:
//
//   - The deck's row axis (KPIs / Performance WIGs / Scalability WIGs) is
//     dropped. Nothing in the coach contract carries it - items have
//     `item_order` within a section and no row/tier dimension at all
//     (08-fe-handoff.md §2) - so a row axis could only ever have been faked.
//   - The deck's "Product WIGs" column is dropped. The API's section enum is
//     a fixed four - `financial_wig`, `customer_wig`, `operations_wig`,
//     `people_wig` (08-fe-handoff.md §1) - and Product is not among them, so
//     it had nothing to render anyway.
//
// What's left maps 1:1 onto what the contract actually guarantees: exactly 4
// sections, ordered by `sequence`, each with N items ordered by `item_order`.
// Columns are rendered straight from `sections[]` rather than from a
// hardcoded list of the 4 keys - so a backend change to the enum or the order
// shows up here as-is instead of silently rendering an empty fixed column.
// (Note this means columns appear in API `sequence` order - financial ->
// customer -> operations -> people - which is the reverse of the deck's
// left-to-right reading. The API's own walk order is the CEO's authoring
// order and the one the coach reviewed in, so it wins.)
//
// Design cues taken from Strategy Canvas, per the same visual-parity goal as
// the rest of this screen: the 5px colored accent bar per column
// (CanvasBand), read-only StatusPills reusing Canvas's own component, a
// skeleton that mirrors the real grid instead of a spinner, and per-column
// item counts in the header.
// ─────────────────────────────────────────────────────────────────────────────

const selectBlueprintByUniqueId = (state) => state.blueprintByUniqueId;
const selectBlueprintLoading = (state) => state.blueprintLoading;
const selectReviewByUniqueId = (state) => state.reviewByUniqueId;

// Column accent colors, assigned positionally for the same reason
// ExecutionBlueprintReviewHelpers.js's getSectionColor is positional: there
// are always exactly 4 sections in a fixed order, so this never needs to know
// how the section keys are spelled. Values are the Art of Scale blueprint
// palette (see the SCSS) rather than Canvas's, since this screen is a
// deliberate re-creation of the blueprint wall chart.
const COLUMN_ACCENTS = ["one", "two", "three", "four"];

// ⚠️ `AUTHORING_NOTICE` was deleted here on 2026-09-03. It mapped the CEO's
// authoring status (COACH-API.md §3) to the chip labels Empty / In progress /
// Awaiting confirmation / Reopened / Flagged. Both status markers were removed
// from the card on instruction — see `ItemCard`.

// Blueprint Mode's own payload has "no review affordances", so it carries
// neither the coach's decisions nor the comment counts. Review Mode's payload
// for the same blueprint is already cached in this very store, so they can be
// read across from it. If Review Mode hasn't been loaded yet this is simply
// empty and no chips render, rather than showing anything invented.
//
// ⚠️ NARROWED TO COMMENT COUNTS (2026-09-03). It also carried `reviewStatus`
// for the accepted/to-discuss pill, which is gone. The cross-read itself still
// earns its keep for the comment count — a card that has a conversation on it
// is worth marking while reading the chart — but note that this is now the
// ONLY reason this component touches Review Mode's payload at all. If the
// comment chip ever goes too, the whole index and its `useEffect` go with it.
const buildReviewIndex = (review) => {
  const index = {};
  (review?.sections || []).forEach((section) => {
    (section.items || []).forEach((item) => {
      index[item.execution_blueprint_item_id] = {
        commentCount: (item.comments || []).length,
      };
    });
  });
  return index;
};

const BlueprintModeSkeleton = () => (
  <div className="bpm-grid bpm-skeleton" aria-hidden="true">
    <div className="bpm-bhag bpm-shimmer" />
    <div className="bpm-columns">
      {[0, 1, 2, 3].map((column) => (
        <div className="bpm-column" key={column}>
          <div className="bpm-column-header bpm-shimmer" />
          <div className="bpm-column-body">
            {[0, 1, 2].map((card) => (
              <div className="bpm-card bpm-card-skeleton" key={card}>
                <div className="bpm-shimmer bpm-shimmer-line bpm-w-70" />
                <div className="bpm-shimmer bpm-shimmer-line bpm-w-100" />
                <div className="bpm-shimmer bpm-shimmer-line bpm-w-40" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ItemCard = ({ item, reviewMeta }) => {
  const commentCount = reviewMeta?.commentCount || 0;

  return (
    <div className="bpm-card">
      <div className="bpm-card-top">
        <span className="bpm-card-label">{item.label}</span>
      </div>

      {item.value ? (
        <p className="bpm-card-value">{item.value}</p>
      ) : (
        <p className="bpm-card-value bpm-card-value--empty">Not filled in yet</p>
      )}

      {/* ⚠️ NO "Why:" LINE (2026-09-03, on instruction: removed "from every
          block"). This rendered `item.rationale`.

          `rationale` is still on the item, still round-trips through every
          edit, and is still what the agent writes when it proposes a WIG —
          nothing has been dropped from the data. It is simply no longer
          printed on this surface. The CEO's own draft cards keep it, moved
          behind a tooltip on the same pass.

          The reasoning that applies everywhere it went: a rationale is read
          once, while deciding whether to keep a WIG. Printed under every item
          it doubled each row's height and pushed the values — the things
          actually being compared — apart. */}

      {/* ⚠️ NO STATUS OF EITHER KIND (2026-09-03, on instruction). Two
          separate markers went from this card, and they were different axes:

            - The coach's own REVIEW decision (`StatusPill`, Accepted / To
              discuss), read across from Review Mode's cached payload.
            - The CEO's AUTHORING state (`AUTHORING_NOTICE` → the warning chip:
              Empty, In progress, Awaiting confirmation, Reopened, Flagged).

          Both are gone; only the comment count remains, which is a pointer to
          a conversation rather than a status.

          This actually returns the view to its contract. COACH-API.md §1 #7
          specifies Blueprint Mode as a "read-only view, items grouped by
          section, **no review affordances**" — the pill was a review
          affordance that had been read in from another payload precisely
          because this one does not carry it. The wall chart is now what it
          says it is: the blueprint as the CEO built it. Decisions live in
          Review Mode, which is one tab away. */}

      {commentCount > 0 && (
        <div className="bpm-card-meta">
          <span className="bpm-comment-chip">
            <CommentIcon size={11} />
            {commentCount}
          </span>
        </div>
      )}
    </div>
  );
};

const ExecutionBlueprintBlueprintMode = ({ uniqueId, companyName }) => {
  const blueprintByUniqueId = useExecutionBlueprintReviewStoreSelector(
    selectBlueprintByUniqueId,
  );
  const blueprintLoading = useExecutionBlueprintReviewStoreSelector(
    selectBlueprintLoading,
  );
  const reviewByUniqueId = useExecutionBlueprintReviewStoreSelector(
    selectReviewByUniqueId,
  );

  const blueprint = blueprintByUniqueId[uniqueId];
  const review = reviewByUniqueId[uniqueId];

  // Fetched on first switch into this mode rather than alongside the review
  // on mount - a coach who never opens Blueprint Mode shouldn't pay for the
  // call, and the store caches by uniqueId so switching back is free.
  useEffect(() => {
    useExecutionBlueprintReviewStore.getState().fetchBlueprintMode(uniqueId);
  }, [uniqueId]);

  const reviewIndex = useMemo(() => buildReviewIndex(review), [review]);

  if (blueprintLoading && !blueprint) return <BlueprintModeSkeleton />;

  const sections = blueprint?.sections || [];

  if (!blueprint || sections.length === 0) {
    return (
      <div className="bpm-grid">
        <div className="bpm-empty-state">
          {blueprint
            ? "This blueprint doesn't have any sections yet."
            : "Couldn't load the blueprint. Try switching back into Review Mode and returning."}
        </div>
      </div>
    );
  }

  return (
    <div className="bpm-grid">
      {/* The deck heads this layout with a BHAG banner, but BHAG isn't in the
          coach contract anywhere - so the banner is shown only if the payload
          actually turns out to carry it (see normalizeBlueprint). No
          placeholder in its absence: an empty teal bar would read as missing
          data rather than as a field that was never part of this view. */}
      {blueprint.bhag && (
        <div className="bpm-bhag">
          <span className="bpm-bhag-cap">BHAG</span>
          <span className="bpm-bhag-value">{blueprint.bhag}</span>
        </div>
      )}

      <div className="bpm-columns">
        {sections.map((section, index) => {
          const items = section.items || [];
          return (
            <div
              className={`bpm-column bpm-accent-${COLUMN_ACCENTS[index % COLUMN_ACCENTS.length]}`}
              key={section.section_key || index}
            >
              <div className="bpm-column-header">
                <span className="bpm-column-title">{section.label}</span>
                <span className="bpm-column-count">
                  {items.length} {items.length === 1 ? "item" : "items"}
                </span>
              </div>

              <div className="bpm-column-body">
                {items.length === 0 ? (
                  <div className="bpm-column-empty">
                    Nothing captured in this section yet.
                  </div>
                ) : (
                  items.map((item) => (
                    <ItemCard
                      key={item.execution_blueprint_item_id}
                      item={item}
                      reviewMeta={reviewIndex[item.execution_blueprint_item_id]}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bpm-footnote">
        Read-only view of {companyName ? `${companyName}'s` : "this"} Execution
        Blueprint. Switch to Review Mode to accept items or leave comments.
      </div>
    </div>
  );
};

export default ExecutionBlueprintBlueprintMode;
